/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    shape, object, bool, func, string, number,
} from 'prop-types';
import { Link } from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LazyLoad from 'react-lazyload';
import { useUIDSeed } from 'react-uid';
import ReactMarkdown from 'react-markdown';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import generateValidCss from '../../../../helpers/contentstack/generateValidCss';
import parseStyles from '../../../../helpers/contentstack/parseStyles';
import Copy from '../../Copy';
import { ifExternalUrl, elementWidth } from '../helper';
import { trackEvent as track } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import ResponsiveImage from '../../../GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { getPresentationFamily } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsBot } from '../../../../../state/ducks/App/App-Selectors';

const useStyles = makeStyles((theme) => ({
    nUp: (data) => ({
        maxWidth: '1400px',
        backgroundColor: (data.reference[0].background?.add_background) ? data.reference[0].background?.solid_background_color : 'none',
        margin: '0 auto',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        padding: data.reference[0].presentation_style === 'Showcase Container' ? '50px 10px 315px' : '10px 10px 0',
        '@media screen and (max-width: 1023px)': {
            fontSize: '0.8em !important',
        },
        '@media screen and (max-width: 1400px)': {
            paddingBottom: data.reference[0].presentation_style === 'Showcase Container' ? 'calc(225px + 5.8vw)' : '0',
        },
        '@media screen and (max-width: 1050px)': {
            paddingBottom: data.reference[0].presentation_style === 'Showcase Container' ? 'calc(192px + 4vw)' : '0',
        },
        '@media screen and (max-height: 699px)': {
            transform: 'scale(.9)',
        },
    }),
    imageAspectRatio: (data) => ({
        position: 'relative',
        paddingBottom: data.reference[0].reference[0]?.container_attributes?.[0]?.dimensions?.padding_bottom,
    }),
    nUpBgImage: (data) => ({
        backgroundImage: `url("${data.reference[0].background?.images?.url}")`,
        backgroundSize: 'cover',
        backgroundPositionY: 'bottom',
    }),
    fullWidth: {
        maxWidth: '100%',
        width: '100%',
        '& $background_stroke': {
            '&::after': {
                width: '75%',
                margin: '0 auto',
            },
        },
    },
    header: {
        textAlign: 'center',
        width: '100%',
        margin: '22px 0 8px',
        lineHeight: '.7em',
        letterSpacing: '.4px',
        fontSize: '1.4em',
        fontWeight: '300',
        '& h1,h2,h3,h4,h5,h6,p': {
            margin: '0px 0 15px 0',
            fontSize: 'inherit',
            fontWeight: 'inherit',
        },
    },
    heading_paragraph_wrapper: {
        width: '100%',
        marginTop: '15px',
        marginBottom: '20px',
    },
    heading_paragraph: {
        margin: '0 auto',
        textAlign: 'center',
        width: '80%',
        lineHeight: '1.5em',
        letterSpacing: '.4px',
        fontSize: '1.4em',
        '& h1,h2,h3,h4,h5,h6,p': {
            margin: '0px 0 15px 0',
            fontSize: 'inherit',
            fontWeight: 'inherit',
        },
    },
    panel: {
        height: '100%',
        textDecoration: 'none',
        backgroundSize: 'cover',
        textAlign: 'center', // TODO: needs to be adjustable in CMS> HD is not centered
        color: '#1f1f1b',
        display: 'block',
        flexDirection: 'column',
        '@media screen and (max-width: 1200px)': {
            '& > span > div': {
                width: '90%',
                margin: '0 auto',
                wordBreak: 'break-word',
                whiteSpace: 'break-spaces',
            },
        },
    },
    panelThreeItems: {
        flexDirection: 'row',
        '& div': {
            height: 'auto',
            width: '88%',
            margin: '0 auto',
        },
    },
    root: {
        boxSizing: 'border-box',
        padding: theme.spacing(3, 0, 0, 0),
    },
    containerLink: {
        textDecoration: 'none',
    },
    imageContainer: {
        height: '90%',
        transition: 'all 0.3s ease 0s',
    },
    imageFixedWidth: {
        maxWidth: '240px',
    },
    posAbs: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    image: {
        '@media screen and (max-width: 1200px)': {
            width: '100% !important',
            height: 'auto !important',
        },
    },
    messageWrapperLarge: {
        width: '95%',
        margin: '0px auto 0',
        '& span': {
            display: 'flex',
        },
    },
    messageWrapperThreeItems: {
        margin: '-24px auto',
        border: theme.palette.gfTabDesktopInputBorder,
        '& div': {
            width: '400px',
        },
        '& span': {
            display: 'flex',
        },
        '@media screen and (max-width: 1200px)': {
            '& div': {
                width: '90%',
            },
        },
    },
    cta: {
        boxSizing: 'border-box',
        display: 'block',
        margin: '0 auto',
        fontSize: '18px',
        fontWeight: '800',
        borderRadius: '2px',
    },
    circleMask: {
        borderRadius: '50%',
    },
    Standard: theme.palette.nup?.Standard,
    Medium: theme.palette.nup?.Medium,
    Small: theme.palette.nup?.Small,
    Large: theme.palette.nup?.Large,
    Auto: {
        maxWidth: '100%',
    },
    background_stroke: {
        display: 'block',
        width: '100%',
        textAlign: 'center',
        '& p': {
            color: theme.palette.cta1,
            margin: '0 auto',
            padding: '0 .5em',
            textTransform: 'uppercase',
            fontSize: '1em',
            fontWeight: 400,
            lineHeight: '2.5em',
            display: 'inline-block',
            background: '#fff',
        },
        '&::after': {
            content: '""',
            position: 'relative',
            borderBottom: 'solid 1px #2f2f2f',
            display: 'block',
            width: '100%',
            top: '-1.37em',
            zIndex: '-1',
        },
    },
    setBackgroundImage: {
        margin: '0 auto',
    },
    dividers: (data) => ({
        backgroundSize: '100% 100%',
        '& a': {
            borderRightColor: data.reference[0].vertical_entry_dividers?.divider_color || '#ccc',
            borderRightSize: data.reference[0].vertical_entry_dividers?.width || '1px',
            borderRightStyle: 'solid',
        },
        '& a:last-child': {
            borderRight: 'none',
        },
    }),
    footerContainer: {
        margin: '10px auto 20px',
    },
    footerLink: {
        fontSize: '20px',
        textDecoration: 'none',
        color: theme.palette.black,
        fontFamily: 'latobold',
    },
    showcaseHeadingFirstWord: {
        color: '#5c656b',
    },
    showcaseHeadingRemaining: {
        color: '#4793b1',
    },
}));

const NUpDesktop = ({
    data, fixedMessage, trackEvent, isBot, presentationFamily,
}) => {
    if (!data?.reference?.[0]) return <></>;
    const classes = useStyles(data);

    const [scale, setScale] = useState(false);
    const [blockKey, setKey] = useState(0);
    const handleMouseHover = (key, isHover) => {
        setScale(isHover);
        setKey(key);
    };

    const transform = {
        transform: `scale(${1.06})`,
    };
    if (data.reference?.[0]) {
        const itemList = data?.reference[0];
        const hasDividers = () => itemList.vertical_entry_dividers?.add_vertical_dividers;

        const renderHeading = (nupData) => {
            /*
             * @description handles conditional header rendering and avoids nested ternaries in markup.
             * @param {object} nup data
             * @return heading markup for eithe standard or t3 showcase heading
            */
            const showcaseHeader = (header) => {
                /*
                 * @description handles params and markup for larger showcase nup header as designed by t3.
                 * @param {object} header data
                 * @return markup with 2 font colors as described in classes in this component.
                */
                const firstSpace = header.search(' ');
                const firstWord = header.substring(0, firstSpace);
                const remainingString = header.substring(firstSpace);
                return (
                    <h2 className={`${classes.header} ${itemList.heading_styles?.background_stroke && classes.background_stroke}`} style={parseStyles(itemList.heading_styles?.font_styles)}>
                        <span className={classes.showcaseHeadingFirstWord}>{firstWord}</span>
                        <span className={classes.showcaseHeadingRemaining}>{remainingString}</span>
                    </h2>
                );
            };
            if (nupData.heading && itemList.presentation_style !== 'Showcase Container') {
                return (
                    <div
                        className={`${classes.header} ${nupData.heading_styles?.background_stroke && classes.background_stroke}`}
                        style={parseStyles(nupData.heading_styles?.font_styles)}
                        data-testid="heading-font-styles"
                    ><ReactMarkdown source={nupData.heading} />
                    </div>
                );
            }
            if (nupData.presentation_style === 'Showcase Container') {
                return showcaseHeader(nupData.heading);
            }
            return null;
        };
        const getPanelStyles = () => {
            const getPanelDividerStyles = () => {
                if (!itemList.vertical_entry_dividers?.add_vertical_dividers) return {};
                const dividerObj = itemList.vertical_entry_dividers;
                const dividerStyles = {
                    marginTop: '20px',
                    borderColor: `${dividerObj.divider_color}`,
                    borderWidth: `${dividerObj.width}px`,
                };
                return dividerStyles;
            };
            const panelStyles = getPanelDividerStyles();
            panelStyles.width = elementWidth(itemList).width;
            return panelStyles;
        };
        const seed = useUIDSeed();
        let messageWrapper = classes.messageWrapperLarge;
        let isThreeItems = false;
        if (itemList.reference?.length === 3) { messageWrapper = classes.messageWrapperThreeItems; isThreeItems = true; }

        const copyMessageData = (dataMessageCollection) => {
            const getBackgroundImage = dataMessageCollection?.use_image_as_background;
            return (!getBackgroundImage ? <div className={messageWrapper}><Copy data={dataMessageCollection?.messageCollection} /></div> : null);
        };

        const getPanelImageData = (imageData) => {
            const getBackgroundImage = imageData?.use_image_as_background;
            return (
                getBackgroundImage
                    ? (
                        <div
                            style={{
                                ...(generateValidCss(imageData.container_attributes, true) || {}),
                                backgroundImage: `url(${imageData?.image?.url}?quality=75&auto=webp&optimize={medium})`,
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'cover',
                                backgroundPositionX: imageData.tablet_x_offset || null,
                            }}
                            className={classes.setBackgroundImage}
                            data-testid="nup-backgroundImage"
                        >
                            {!fixedMessage
                                ? (<div style={generateValidCss(imageData?.message_styling, true)}><Copy data={imageData.message_collection} /></div>)
                                : (<div className={messageWrapper}><Copy data={imageData.message_collection} /></div>)}
                        </div>
                    )
                    : (
                        <LazyLoad height={400} offset={400}>
                            <ResponsiveImage
                                style={generateValidCss(imageData.container_attributes, true)}
                                path={imageData?.image?.url}
                                alt=""
                                className={`${classes.image} ${(imageData.container_attributes?.[0]?.circle_mask?.mask && classes.imageFixedWidth)} ${(classes[data?.reference?.[0]?.presentation_style])} ${(imageData.container_attributes?.[0]?.dimensions?.padding_bottom && classes.posAbs)}`}
                                role="presentation"
                            />
                        </LazyLoad>
                    )
            );
        };

        const footer = (fields) => {
            if (fields) {
                const trackable = fields?.link;
                if (trackable || fields?.footer_copy) {
                    return (
                        <div className={classes.footerContainer}>
                            {fields.copy && <ReactMarkdown source={fields.copy} />}
                            {trackable?.link?.href && (
                                <Link
                                    aria-hidden="true"
                                    to={trackable?.link?.href}
                                    title={trackable?.link?.title}
                                    className={classes.footerLink}
                                    data-ga-category={trackable?.tracking_event_category || ''}
                                    data-ga-action={trackable?.tracking_event_action || ''}
                                    data-ga-label={trackable?.tracking_event_label || ''}
                                    onClick={(e) => {
                                        trackEvent({
                                            eventCategory: trackable.tracking_event_category || '',
                                            eventAction: trackable.tracking_event_action || '',
                                            eventLabel: trackable.tracking_event_label || '',
                                            eventName: 'feature_interaction', // Requird for GA4 feature_click
                                        });
                                        ifExternalUrl(e, trackable?.link.href);
                                    }}
                                >{trackable?.link?.title}
                                </Link>
                            )}
                        </div>
                    );
                }
            }
            return null;
        };

        return (
            <div
                className={`
                    ${classes.nUp}
                    ${itemList.full_width ? classes.fullWidth : ''}
                    ${hasDividers() ? classes.dividers : ''}
                    ${itemList.background?.images ? classes.nUpBgImage : ''}
                `}
                data-testid="bg-elem"
            >
                {renderHeading(itemList)}
                {
                    itemList.heading_paragraph ? (
                        <div className={classes.heading_paragraph_wrapper}>
                            <div className={classes.heading_paragraph}>
                                <ReactMarkdown source={itemList.heading_paragraph} />
                            </div>
                        </div>
                    ) : null
                }
                {
                    itemList.reference?.map((i, key) => (
                        <>
                            <Link
                                onMouseOver={() => handleMouseHover(key, true)}
                                onMouseLeave={() => handleMouseHover(key, false)}
                                key={seed(i)}
                                aria-hidden="true"
                                tabIndex="-1"
                                to={i.linking?.link?.href}
                                title={i.linking?.link?.title}
                                className={`${classes.panel} ${isThreeItems && classes.panelThreeItems}`}
                                style={getPanelStyles()}
                                data-ga-category={i.linking?.tracking_event_category || ''}
                                data-ga-action={i.linking?.tracking_event_action || ''}
                                data-ga-label={i.linking?.tracking_event_label || ''}
                                onClick={(e) => {
                                    trackEvent({
                                        eventCategory: i.linking?.tracking_event_category || '',
                                        eventAction: i.linking?.tracking_event_action || '',
                                        eventLabel: i.linking?.tracking_event_label || '',
                                        eventName: 'feature_interaction', // Requird for GA4 feature_click
                                    });
                                    ifExternalUrl(e, i.linking?.link?.href);
                                }}
                            >
                                {i.image
                                    /* If a backgroud image is provided, it will render as default product tile
                                    else it will render a div of provided dimension / presentation style */
                                    ? (
                                        <div>
                                            <div
                                                style={presentationFamily !== 'food' && scale && key === blockKey ? transform : null}
                                                className={`${classes.imageContainer} ${i.container_attributes?.[0]?.dimensions?.padding_bottom && classes.imageAspectRatio}`}
                                            >
                                                {isBot ? <img src={i?.image?.url} alt={i.linking?.link?.title} /> : getPanelImageData(i)}
                                            </div>
                                            <div style={generateValidCss(i?.message_styling, true)} data-testid="nup-panel-msg">
                                                {(!fixedMessage && !i?.use_image_as_background) ? (<Copy data={i.message_collection} />) : (copyMessageData(i))}
                                            </div>
                                        </div>
                                        /* Added Copy components instead of manual switch case
                                        to avoid code repetition and to add more functionality to n-up panel.
                                        We can use Panel component directly but background image logic has not been implemented in it yet */
                                    )
                                    : (
                                        <div className={`${classes[data?.reference[0]?.presentation_style]} ${messageWrapper}`} style={generateValidCss(i.container_attributes, true)}>
                                            <div style={generateValidCss(i?.message_styling, true)} data-testid="nup-panel-msg">
                                                <Copy data={i.message_collection} />
                                            </div>
                                        </div>
                                    )}
                            </Link>
                        </>
                    ))
                }
                {itemList.footer && footer(itemList.footer)}
            </div>
        );
    }
    return <></>;
};

NUpDesktop.propTypes = {
    isBot: bool,
    data: shape({
        disable_view_more: bool,
        display_on: string,
        full_width: bool,
        heading: string,
        heading_styles: shape({
            background_stroke: bool,
            font_styles: shape({
                font_color: object,
                font_family: string,
                font_size: string,
                font_weight: number,
                letter_spacing: string,
                line_height: string,
                text_align: string,
                text_decoration: string,
            }),
        }),
        footer: shape({
            footer_copy: string,
            footer_link: shape({
                title: string,
                href: string,
            }),
        }),
        background: shape({
            add_background: bool,
            images: object,
            solid_background_color: object,
        }),
        vertical_entry_dividers: shape({
            add_vertical_dividers: bool,
            divider_color: object,
            width: string,
        }),
    }).isRequired,
    reference: object.isRequired,
    fixedMessage: bool.isRequired,
    trackEvent: func.isRequired,
    presentationFamily: string.isRequired,
};

NUpDesktop.defaultProps = {
    isBot: false,
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

const mapStateToProps = (state) => ({
    presentationFamily: getPresentationFamily(state),
    isBot: getIsBot(state),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(NUpDesktop);
